// Enhetspalett (APP-6): velg tilhørighet + echelon + type, og klikk på kartet
// for å plassere. GNÅ-stylet flytende panel. Ren DOM, ingen rammeverk.
//
// Paletten har TO moduser, og forskjellen er ikke kosmetisk:
//  · PLASSER — valget lager en NY enhet (armerer kartklikket, eller slipper den
//    direkte på et punkt fra høyreklikk).
//  · ENDRE   — valget OPPDATERER en enhet som allerede står på kartet, med samme
//    id, samme posisjon og samme navn.
//
// ⚠ HVA SOM GIKK GALT: paletten kunne bare lage nye enheter. En enhet som hadde
// fått feil symbol kunne derfor ikke rettes — bare slettes og tegnes på nytt. Det
// gir enheten NY id, og var den koblet til ORBAT (id = ORBAT-unit-id) var
// koblingen tapt. Endre-modusen åpner derfor FORHÅNDSUTFYLT med enhetens
// nåværende tilhørighet og echelon, slik at operatøren retter én ting og ikke må
// gjenskape alt han allerede hadde satt.
import {
  UNIT_TYPES, ECHELONS, AFFIL_LABEL, buildSidc, parseSidc, typeForSidc, type Affil,
} from './app6.js';

const AFFILS: Affil[] = ['friend', 'hostile', 'neutral', 'unknown'];

/** Enheten som rettes (id-en er det viktigste — den skal BEHOLDES). */
export interface EndreMaal {
  id: string;
  label: string;
  sidc: string;
}

export class UnitPalette {
  private el: HTMLElement;
  private open = false;
  private aff: Affil = 'friend';
  private echelon = '-';
  /** Satt når paletten åpnes fra høyreklikk → plasser direkte på punktet. */
  private targetCoord: [number, number] | null = null;
  /** Satt når paletten retter en EKSISTERENDE enhet i stedet for å lage en ny. */
  private endrer: EndreMaal | null = null;

  // Referanser til kontrollene, så modusbytte kan speile tilstanden i DOM. Uten
  // dem ville «forhåndsutfylt» bare vært et internt felt operatøren ikke ser.
  private tittelEl!: HTMLElement;
  private notatEl!: HTMLElement;
  private affBtns = new Map<Affil, HTMLButtonElement>();
  private echSel!: HTMLSelectElement;
  private typeBtns = new Map<string, HTMLButtonElement>();

  constructor(
    private onPick: (sidc: string, label: string, coord: [number, number] | null) => void,
    /** Kalles i ENDRE-modus. Kalleren skal mutere enheten med `id`, ikke lage ny. */
    private onEndre?: (id: string, sidc: string, typeLabel: string) => void,
  ) {
    this.el = this.build();
    document.body.appendChild(this.el);
  }

  toggle() {
    this.targetCoord = null;
    this.endrer = null;
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) this.syncKontroller();
  }
  /** Åpne paletten forhåndsrettet mot et kartpunkt (fra høyreklikk-menyen). */
  openAt(lon: number, lat: number) {
    this.targetCoord = [lon, lat];
    this.endrer = null;
    this.open = true;
    this.el.classList.add('open');
    this.syncKontroller();
  }
  /**
   * Åpne paletten for å RETTE en enhet som står på kartet. Tilhørighet og echelon
   * leses ut av enhetens nåværende SIDC, og typen den har nå markeres — det er
   * dette som gjør at operatøren kan bytte bare symbolet uten å sette alt på nytt.
   */
  openForEndring(m: EndreMaal) {
    const d = parseSidc(m.sidc);
    this.aff = d.aff;
    this.echelon = d.echelon;
    this.endrer = m;
    this.targetCoord = null;
    this.open = true;
    this.el.classList.add('open');
    this.syncKontroller();
  }
  close() {
    this.open = false;
    this.targetCoord = null;
    this.endrer = null;
    this.el.classList.remove('open');
  }

  /** Er paletten i endre-modus? (til test og til modus-visning) */
  get endrerId(): string | null { return this.endrer?.id ?? null; }

  /** Speil `aff`/`echelon`/modus ned i kontrollene. */
  private syncKontroller() {
    const e = this.endrer;
    this.tittelEl.textContent = e ? 'Endre type/symbol' : 'Plasser enhet';
    if (e) {
      // Sier eksplisitt at id og navn beholdes — ellers tror operatøren med god
      // grunn at han er i ferd med å lage en ny enhet ved siden av den gamle.
      this.notatEl.textContent = `«${e.label}» — beholder id og posisjon. Velg riktig symbol.`;
      this.notatEl.hidden = false;
    } else {
      this.notatEl.textContent = '';
      this.notatEl.hidden = true;
    }
    for (const [a, b] of this.affBtns) b.classList.toggle('active', a === this.aff);
    this.echSel.value = this.echelon;
    const naa = e ? typeForSidc(e.sidc) : undefined;
    for (const [id, b] of this.typeBtns) b.classList.toggle('naa', !!naa && naa.id === id);
  }

  private build(): HTMLElement {
    const panel = div('unit-palette');

    const head = div('unit-palette-head');
    this.tittelEl = span('unit-palette-title', 'Plasser enhet');
    head.append(this.tittelEl);
    const x = document.createElement('button');
    x.className = 'unit-palette-x'; x.textContent = '×'; x.onclick = () => this.close();
    head.append(x);
    panel.append(head);

    this.notatEl = div('unit-palette-note');
    this.notatEl.hidden = true;
    panel.append(this.notatEl);

    // Tilhørighet (farge-segmentert).
    const affRow = div('unit-affil');
    for (const a of AFFILS) {
      const b = document.createElement('button');
      b.className = `unit-affil-btn aff-${a}` + (a === this.aff ? ' active' : '');
      b.textContent = AFFIL_LABEL[a];
      b.onclick = () => {
        this.aff = a;
        for (const el of affRow.querySelectorAll('.unit-affil-btn')) el.classList.remove('active');
        b.classList.add('active');
      };
      affRow.append(b);
      this.affBtns.set(a, b);
    }
    panel.append(affRow);

    // Echelon.
    const echRow = div('unit-echelon');
    echRow.append(span('unit-field-label', 'Echelon'));
    const sel = document.createElement('select');
    sel.className = 'unit-echelon-sel';
    for (const e of ECHELONS) {
      const o = document.createElement('option');
      o.value = e.code; o.textContent = e.label;
      sel.append(o);
    }
    sel.onchange = () => (this.echelon = sel.value);
    echRow.append(sel);
    this.echSel = sel;
    panel.append(echRow);

    // Type-rutenett.
    const grid = div('unit-grid');
    for (const t of UNIT_TYPES) {
      const b = document.createElement('button');
      b.className = 'unit-type';
      b.textContent = t.label;
      b.onclick = () => {
        const sidc = buildSidc(t.func, t.dim, this.aff, this.echelon);
        // Rekkefølgen betyr noe: `close()` nullstiller BÅDE `endrer` og
        // `targetCoord`, så begge må leses av FØR paletten lukkes.
        const e = this.endrer;
        const coord = this.targetCoord;
        this.close();
        if (e) this.onEndre?.(e.id, sidc, t.label);
        else this.onPick(sidc, t.label, coord);
      };
      grid.append(b);
      this.typeBtns.set(t.id, b);
    }
    panel.append(grid);

    return panel;
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
